// Purpose: Object utilities.

/**
 * A version of `T` where every value has `null` and `undefined` stripped from its type,
 * -? strips optionality and Exclude<T[K], null | undefined> removes nullish types
 */
export type OmitNullish<T> = {
  [K in keyof T]-?: Exclude<T[K], null | undefined>
}

/**
 * Returns a shallow copy of `obj` with all keys whose value is `null` or `undefined` removed.
 * Replaces verbose ternary-spread patterns like:
 *   `...(value ? { key: value } : {})`
 * with a plain property assignment inside an `omitNullish({})` call.
 */
export function omitNullish<T extends object>(obj: T): OmitNullish<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null)) as OmitNullish<T>
}

// clone an object
export function clone<T>(obj: T) {
  try {
    return JSON.parse(JSON.stringify(obj)) as T
  } catch (error) {
    return obj
  }
}

// compare two objects recursively
export function isObjectEqual(obj1: any, obj2: any) {
  if (obj1 === obj2) return true
  if (obj1 == null && obj2 != null) return false
  if (obj1 != null && obj2 == null) return false
  if (typeof obj1 !== typeof obj2) return false

  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) return false

    for (const key in obj1) {
      if (!isObjectEqual(obj1[key], obj2[key])) return false
    }

    return true
  }

  return false
}

// merge two objects recursively
export function merge(obj1: any, obj2: any) {
  if (obj1 == null && obj2 == null) return null
  if (obj1 != null && obj2 == null) return obj1
  if (obj1 == null && obj2 != null) return obj2

  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const merged = { ...obj1 }

    for (const key in obj2) {
      merged[key] = merge(obj1[key], obj2[key])
    }

    return merged
  }

  return obj2
}
